import * as THREE from 'three';

// The Miami skyline plate tools/build-bayfront-skyline.mjs cuts for
// visualizers/bayfront.js: the city with its windows dark (off), lit (on),
// the lossless tower id map, and the manifest with the measured ground line.
// Same preload/get shape as alley-assets.js, and the same rule: the preload
// is a WIRING POINT every entry point must call (main, main-radio,
// main-embedded, play-boot, landing-boot, demo-boot, the capture harness);
// a forgotten one is silent - the getter returns null and Bayfront falls
// back to its procedural buildings.
//
// The manifest AND both texture sets (full and low) load at startup, like
// the alley's: loaded lazily at the first Bayfront they would appear as a
// +3 texture growth on the scene-leak test's second cycle, which compares a
// composition to itself (the test cannot tell a cache from a leak). About
// 18 MB of GPU memory for the two sets. The ids map is DATA: NearestFilter,
// no mipmaps, no colour space.
const MANIFEST_URL = 'textures/bayfront.json';

let _manifest = null;
let _promise = null;
const _textures = {};

function loadTexture(url, configure) {
  return new Promise(function (resolve, reject) {
    new THREE.TextureLoader().load(url, function (t) { configure(t); t.needsUpdate = true; resolve(t); }, undefined, function () {
      reject(new Error(url + ' failed to load'));
    });
  });
}
function photo(t) {
  t.colorSpace = THREE.SRGBColorSpace;
  t.generateMipmaps = false;
  t.minFilter = THREE.LinearFilter;
  t.magFilter = THREE.LinearFilter;
  t.wrapS = t.wrapT = THREE.ClampToEdgeWrapping;
  // premultiplied by the build (the sky's alpha): the shader divides back
  t.premultiplyAlpha = false;
}
function data(t) {
  t.colorSpace = THREE.NoColorSpace;
  t.generateMipmaps = false;
  t.minFilter = THREE.NearestFilter;
  t.magFilter = THREE.NearestFilter;
  t.wrapS = t.wrapT = THREE.ClampToEdgeWrapping;
}

export function preloadBayfrontSkyline() {
  if (_promise) return _promise;
  _promise = fetch(MANIFEST_URL)
    .then(function (r) {
      if (!r.ok) throw new Error('bayfront.json HTTP ' + r.status);
      return r.json();
    })
    .then(function (m) {
      if (!m.on) throw new Error('bayfront.json has no lit picture');
      _manifest = m;
      // both sets, now - see the header
      return Promise.all([loadBayfrontSkyline(false), loadBayfrontSkyline(true)]).then(function () { return m; });
    })
    .catch(function (err) {
      console.warn('[party-mode] skyline plate unavailable, Bayfront keeps its procedural buildings', err);
      _manifest = null;
      return null;
    });
  return _promise;
}

export function getBayfrontSkyline() {
  return _manifest;
}

// Resolves to { on, off, ids, manifest } for a tier (low: the 1024-wide
// pair), off/ids null when the manifest lacks them (static plate), or null
// when there is no plate at all. Textures are shared across instances.
export function loadBayfrontSkyline(low) {
  return Promise.resolve(_manifest).then(function (m) {
    if (!m) return null;
    const key = low ? 'low' : 'full';
    if (_textures[key]) return _textures[key];
    const onName = low && m.onLow ? m.onLow : m.on;
    const offName = low && m.offLow ? m.offLow : m.off;
    const idsName = low && m.idsLow ? m.idsLow : m.ids;
    const jobs = [loadTexture('textures/' + onName, photo)];
    jobs.push(offName ? loadTexture('textures/' + offName, photo).catch(function () { return null; }) : Promise.resolve(null));
    jobs.push(idsName ? loadTexture('textures/' + idsName, data).catch(function () { return null; }) : Promise.resolve(null));
    _textures[key] = Promise.all(jobs).then(function (r) {
      return { on: r[0], off: r[1], ids: r[1] ? r[2] : null, manifest: m };
    }).catch(function (err) {
      console.warn('[party-mode] skyline plate textures failed', err);
      _textures[key] = null;
      return null;
    });
    return _textures[key];
  });
}
